import { useState, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import type {
  ManualXeroInvoiceRequest,
  XeroInvoiceStatus,
  ImportResponse,
  Organisation,
} from '../../types'
import { createXeroInvoiceFromForm } from '../../services/licensing'
import { listOrganisations } from '../../services/admin'
import { logger } from '../../utils/logger'
import { isValidUuid } from './XeroManualForm.helpers'

export interface XeroFormDataShape {
  xeroOrganisationId: string
  xeroInvoiceNumber: string
  xeroTenantId: string
  xeroInvoiceId: string
  invoiceStatus: XeroInvoiceStatus
  currencyCode: string
  invoiceDate: string
  dueDate: string
  fullyPaidOn: string
  entitlementId: string
  purchaseRequestId: string
}

interface UseXeroFormArgs {
  initialData?: Partial<ManualXeroInvoiceRequest> | undefined
  onSuccess?: ((result: ImportResponse) => void) | undefined
  onError?: ((error: Error) => void) | undefined
}

export interface UseXeroFormReturn {
  formData: XeroFormDataShape
  setFormData: React.Dispatch<React.SetStateAction<XeroFormDataShape>>
  amountDueStr: string
  setAmountDueStr: React.Dispatch<React.SetStateAction<string>>
  amountPaidStr: string
  setAmountPaidStr: React.Dispatch<React.SetStateAction<string>>
  amountCreditedStr: string
  setAmountCreditedStr: React.Dispatch<React.SetStateAction<string>>
  isSubmitting: boolean
  result: ImportResponse | null
  error: string | null
  organisations: Organisation[]
  selectedOrgId: string
  setSelectedOrgId: React.Dispatch<React.SetStateAction<string>>
  isLoadingOrgs: boolean
  submitForm: (args: XeroSubmitArgs) => Promise<void>
}

export interface XeroSubmitArgs {
  formData: XeroFormDataShape
  selectedOrgId: string
  amountDueStr: string
  amountPaidStr: string
  amountCreditedStr: string
}

function applyIdAndAmountFields(args: {
  data: ManualXeroInvoiceRequest
  formData: XeroFormDataShape
  selectedOrgId: string
  amountCredited: number
}): void {
  const { data, formData, selectedOrgId, amountCredited } = args
  if (selectedOrgId) data.organisationId = selectedOrgId
  if (formData.xeroTenantId) data.xeroTenantId = formData.xeroTenantId
  if (formData.xeroInvoiceId) data.xeroInvoiceId = formData.xeroInvoiceId
  if (amountCredited > 0) data.amountCredited = amountCredited
  if (formData.currencyCode) data.currencyCode = formData.currencyCode
}

function applyDateAndLinkingFields(args: {
  data: ManualXeroInvoiceRequest
  formData: XeroFormDataShape
}): void {
  const { data, formData } = args
  if (formData.invoiceDate) data.invoiceDate = formData.invoiceDate
  if (formData.dueDate) data.dueDate = formData.dueDate
  if (formData.fullyPaidOn) data.fullyPaidOn = formData.fullyPaidOn
  if (formData.entitlementId) data.entitlementId = formData.entitlementId
  if (formData.purchaseRequestId) data.purchaseRequestId = formData.purchaseRequestId
}

export function buildXeroInvoiceRequest(args: {
  formData: XeroFormDataShape
  selectedOrgId: string
  amountDue: number
  amountPaid: number
  amountCredited: number
}): ManualXeroInvoiceRequest {
  const { formData, selectedOrgId, amountDue, amountPaid, amountCredited } = args
  const cleanedData: ManualXeroInvoiceRequest = {
    xeroOrganisationId: formData.xeroOrganisationId,
    xeroInvoiceNumber: formData.xeroInvoiceNumber,
    invoiceStatus: formData.invoiceStatus,
    amountDue,
    amountPaid,
  }
  applyIdAndAmountFields({ data: cleanedData, formData, selectedOrgId, amountCredited })
  applyDateAndLinkingFields({ data: cleanedData, formData })
  return cleanedData
}

const XERO_FORM_DEFAULTS = {
  xeroOrganisationId: '',
  xeroInvoiceNumber: '',
  xeroTenantId: '',
  xeroInvoiceId: '',
  invoiceStatus: 'PAID' as XeroInvoiceStatus,
  currencyCode: 'EUR',
  invoiceDate: '',
  dueDate: '',
  fullyPaidOn: '',
  entitlementId: '',
  purchaseRequestId: '',
} satisfies XeroFormDataShape

function pickDefinedXeroFields(
  data: Partial<ManualXeroInvoiceRequest>
): Partial<XeroFormDataShape> {
  const result: Partial<XeroFormDataShape> = {}
  const fields: (keyof XeroFormDataShape & keyof ManualXeroInvoiceRequest)[] = [
    'xeroOrganisationId',
    'xeroInvoiceNumber',
    'xeroTenantId',
    'xeroInvoiceId',
    'invoiceStatus',
    'currencyCode',
    'invoiceDate',
    'dueDate',
    'fullyPaidOn',
    'entitlementId',
    'purchaseRequestId',
  ]
  for (const field of fields) {
    const val = data[field]
    if (val !== undefined) (result as Record<string, unknown>)[field] = val
  }
  return result
}

function buildInitialFormData(
  data: Partial<ManualXeroInvoiceRequest> | undefined
): XeroFormDataShape {
  if (!data) return { ...XERO_FORM_DEFAULTS }
  return { ...XERO_FORM_DEFAULTS, ...pickDefinedXeroFields(data) }
}

function validateXeroSubmit(args: {
  formData: XeroFormDataShape
  errorMsg: string
  uuidMsg: string
}): string | null {
  if (!args.formData.xeroOrganisationId || !args.formData.xeroInvoiceNumber) return args.errorMsg
  if (!isValidUuid(args.formData.xeroOrganisationId)) return args.uuidMsg
  return null
}

interface XeroSubmitDeps {
  submitArgs: XeroSubmitArgs
  t: ReturnType<typeof useTranslation<'admin'>>['t']
  onSuccess: UseXeroFormArgs['onSuccess']
  onError: UseXeroFormArgs['onError']
  setIsSubmitting: React.Dispatch<React.SetStateAction<boolean>>
  setError: React.Dispatch<React.SetStateAction<string | null>>
  setResult: React.Dispatch<React.SetStateAction<ImportResponse | null>>
}

async function executeXeroSubmit(deps: XeroSubmitDeps): Promise<void> {
  const { submitArgs, t, onSuccess, onError, setIsSubmitting, setError, setResult } = deps
  setIsSubmitting(true)
  setError(null)
  setResult(null)
  try {
    const validationError = validateXeroSubmit({
      formData: submitArgs.formData,
      errorMsg: t('licensing.xero.errors.orgAndInvoiceRequired'),
      uuidMsg: t('licensing.xero.errors.invalidUuid'),
    })
    if (validationError) throw new Error(validationError)
    const amountDue = parseFloat(submitArgs.amountDueStr) || 0
    const amountPaid = parseFloat(submitArgs.amountPaidStr) || 0
    const amountCredited = parseFloat(submitArgs.amountCreditedStr) || 0
    const cleanedData = buildXeroInvoiceRequest({
      formData: submitArgs.formData,
      selectedOrgId: submitArgs.selectedOrgId,
      amountDue,
      amountPaid,
      amountCredited,
    })
    const response = await createXeroInvoiceFromForm(cleanedData)
    setResult(response)
    onSuccess?.(response)
  } catch (err) {
    const errorMessage =
      err instanceof Error ? err.message : t('licensing.xero.errors.failedToSave')
    setError(errorMessage)
    onError?.(err instanceof Error ? err : new Error(errorMessage))
  } finally {
    setIsSubmitting(false)
  }
}

export function useXeroManualForm({
  initialData,
  onSuccess,
  onError,
}: UseXeroFormArgs): UseXeroFormReturn {
  const { t } = useTranslation('admin')

  const [formData, setFormData] = useState<XeroFormDataShape>(() =>
    buildInitialFormData(initialData)
  )
  const [amountDueStr, setAmountDueStr] = useState(initialData?.amountDue?.toString() ?? '0')
  const [amountPaidStr, setAmountPaidStr] = useState(initialData?.amountPaid?.toString() ?? '')
  const [amountCreditedStr, setAmountCreditedStr] = useState(
    initialData?.amountCredited?.toString() ?? ''
  )
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [result, setResult] = useState<ImportResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [organisations, setOrganisations] = useState<Organisation[]>([])
  const [selectedOrgId, setSelectedOrgId] = useState<string>('')
  const [isLoadingOrgs, setIsLoadingOrgs] = useState(true)

  useEffect(() => {
    const load = async (): Promise<void> => {
      try {
        setIsLoadingOrgs(true)
        const response = await listOrganisations({ limit: 100 })
        setOrganisations(response.data)
      } catch (err) {
        logger.error('XeroManualForm', 'Failed to load organisations', err)
      } finally {
        setIsLoadingOrgs(false)
      }
    }
    void load()
  }, [])

  const submitForm = useCallback(
    (submitArgs: XeroSubmitArgs): Promise<void> =>
      executeXeroSubmit({ submitArgs, t, onSuccess, onError, setIsSubmitting, setError, setResult }),
    [t, onSuccess, onError]
  )

  return {
    formData, setFormData,
    amountDueStr, setAmountDueStr,
    amountPaidStr, setAmountPaidStr,
    amountCreditedStr, setAmountCreditedStr,
    isSubmitting, result, error,
    organisations, selectedOrgId, setSelectedOrgId, isLoadingOrgs,
    submitForm,
  }
}
